import React, { useEffect, useRef, useState } from 'react';

import { useKeyboard } from '@/shared/hooks/useKeyboard';

import {
    AnimatedCarouselItemState,
    animateScrollLeft,
    applyAnimatedCarouselClick,
    createInitialItemStates,
    getModeFromItemState,
    getTargetScrollLeftPx,
} from './animatedCarouselMode';
import { AnimatedCarouselItem } from './_AnimatedCarouselItem';
import styles from './_AnimatedCarousel.module.css';

//  Константы эквиваленты css-переменным в файле стилей
const CAROUSEL_SLOT = 32.2;
const CAROUSEL_DURATION = 300;

interface AnimatedCarouselProps {
    items: unknown[];
    renderItem(
        item: unknown,
        index: number
    ): {
        key: string;
        active: React.ReactNode;
        default: React.ReactNode;
    };
    onActiveChange?: (activeIndex: number) => void;
}

export const AnimatedCarousel: React.FC<AnimatedCarouselProps> = (props) => {
    const { items, renderItem, onActiveChange } = props;

    const [itemStates, setItemStates] = useState<AnimatedCarouselItemState[]>(() => {
        const result = applyAnimatedCarouselClick({
            states: createInitialItemStates(items.length),
            activeIndex: null,
            clickedIndex: 0,
        });
        return result?.states ?? createInitialItemStates(items.length);
    });
    const [activeIndex, setActiveIndex] = useState<number>(0);

    const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
    const cancelScrollRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!rootEl) {
            return undefined;
        }

        const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const slotPx = CAROUSEL_SLOT * remPx;
        const durationMs = CAROUSEL_DURATION;

        const targetLeft = getTargetScrollLeftPx(activeIndex, slotPx);

        cancelScrollRef.current?.();
        cancelScrollRef.current = animateScrollLeft(rootEl, targetLeft, durationMs);

        return () => cancelScrollRef.current?.();
    }, [activeIndex, rootEl]);

    useEffect(() => {
        rootEl?.focus({ preventScroll: true });
    }, [rootEl]);

    const handleItemClick = (index: number) => {
        const result = applyAnimatedCarouselClick({
            states: itemStates,
            activeIndex,
            clickedIndex: index,
        });

        if (!result) {
            return;
        }

        setItemStates(result.states);
        setActiveIndex(result.activeIndex);
        onActiveChange?.(result.activeIndex);
    };

    useKeyboard({
        target: rootEl,
        onLeft: (event) => {
            event.preventDefault();
            if (activeIndex > 0) {
                handleItemClick(activeIndex - 1);
            }
        },
        onRight: (event) => {
            event.preventDefault();
            if (activeIndex < items.length - 1) {
                handleItemClick(activeIndex + 1);
            }
        },
    });

    return (
        <div ref={setRootEl} className={styles.root} tabIndex={-1}>
            <div className={styles.track}>
                {itemStates.map((state, index) => {
                    const renderedItem = renderItem(items[index], index);

                    return (
                        <AnimatedCarouselItem
                            key={renderedItem.key}
                            activeChild={renderedItem.active}
                            defaultChild={renderedItem.default}
                            mode={getModeFromItemState(state)}
                            onClick={() => handleItemClick(index)}
                        />
                    );
                })}
            </div>
        </div>
    );
};
